import React, { Suspense, useEffect, useRef } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  Navigate,
} from "react-router-dom";
import Nav from "./components/Nav";
import Footer from "./components/Footer";
import RouteCurtain from "./components/RouteCurtain";
import PremiumCursor from "./components/PremiumCursor";
import SiteAtmosphere from "./components/SiteAtmosphere";
import TerminalOverlay from "./components/TerminalOverlay";
import MobileContactBar from "./components/MobileContactBar";
import SmoothScrollRuntime from "./components/SmoothScrollRuntime";
import { trackPageView, trackEvent } from "./lib/analytics";
import { ROUTE_META, LEGACY_REDIRECTS, routeFor } from "./data/routes.mjs";
import { LAZY_PAGES } from "./routes";

function RouteEffects() {
  const location = useLocation();
  const previous = useRef(location.pathname);
  useEffect(() => {
    const hashTarget = () => {
      try {
        return document.getElementById(
          decodeURIComponent(location.hash.slice(1)),
        );
      } catch {
        return null;
      }
    };
    let frame;
    const update = () => {
      const heading = [...document.querySelectorAll("main h1")].find(
        (element) => element.getClientRects().length,
      );
      if (!heading) {
        frame = requestAnimationFrame(update);
        return;
      }
      if (previous.current !== location.pathname) {
        if (location.hash) hashTarget()?.scrollIntoView();
        else window.scrollTo({ top: 0, behavior: "instant" });
        heading.setAttribute("tabindex", "-1");
        heading.focus({ preventScroll: true });
        previous.current = location.pathname;
      } else if (location.hash) {
        hashTarget()?.scrollIntoView();
      }
      trackPageView({ path: location.pathname, search: location.search });
    };
    frame = requestAnimationFrame(update);
    return () => cancelAnimationFrame(frame);
  }, [location.pathname, location.search, location.hash]);
  useEffect(() => {
    const clicked = (event) => {
      const link = event.target.closest?.('a[href^="/contact"]');
      if (!link) return;
      const params = new URL(link.href).searchParams;
      trackEvent("enquiry_cta_click", {
        source: routeFor(location.pathname) ? location.pathname : "other",
        service: params.get("service") || "unspecified",
        package: params.get("package") || "unspecified",
        support: params.get("support") || "unspecified",
        preview_requested: params.get("preview") === "1-page" ? "yes" : "no",
      });
    };
    document.addEventListener("click", clicked);
    return () => document.removeEventListener("click", clicked);
  }, [location.pathname]);
  useEffect(() => {
    const clicked = (event) => {
      const link = event.target.closest?.('a[href^="tel:"], a[href*="wa.me/"]');
      if (!link) return;
      trackEvent(link.getAttribute("href").startsWith("tel:") ? "call_click" : "whatsapp_click", {
        source: routeFor(location.pathname) ? location.pathname : "other",
      });
    };
    document.addEventListener("click", clicked);
    return () => document.removeEventListener("click", clicked);
  }, [location.pathname]);
  return null;
}

export default function App({
  Router = BrowserRouter,
  initialEntries,
  InitialPage,
  initialPageName,
}) {
  const componentFor = (name) =>
    name === initialPageName && InitialPage ? InitialPage : LAZY_PAGES[name];
  return (
    <Router {...(initialEntries ? { initialEntries } : {})}>
      <RouteEffects />
      <RouteCurtain />
      <SmoothScrollRuntime />
      <PremiumCursor />
      <a href="#main-content" className="skip-link" data-menu-background>
        Skip to content
      </a>
      <div className="min-h-screen bg-ink text-cream flex flex-col relative isolate">
        <SiteAtmosphere />
        <TerminalOverlay />
        <Nav />
        <main
          id="main-content"
          tabIndex={-1}
          className="flex-1 relative z-10"
          data-menu-background
        >
          <Suspense
            fallback={
              <div
                className="container-x pt-32 pb-20 min-h-[60vh]"
                role="status"
              >
                Loading page…
              </div>
            }
          >
            <Routes>
              {Object.entries(ROUTE_META).map(([path, meta]) => {
                const Page = componentFor(meta.component);
                return <Route key={path} path={path} element={<Page />} />;
              })}
              {Object.entries(LEGACY_REDIRECTS).map(([path, to]) => (
                <Route
                  key={path}
                  path={path}
                  element={<Navigate to={to} replace />}
                />
              ))}
              <Route
                path="*"
                element={React.createElement(componentFor("NotFound"))}
              />
            </Routes>
          </Suspense>
        </main>
        <div data-menu-background className="pb-14 md:pb-0">
          <div className="relative z-10"><Footer /></div>
        </div>
        <MobileContactBar />
      </div>
    </Router>
  );
}
